import React, { useCallback, useMemo, useRef } from 'react';
import { TabsContainer } from '../shared/TabsContainer';

export type TabType =
    | 'all'
    | 'general'
    | 'operational'
    | 'regulations'
    | 'uscg'
    | 'coating'
    | 'safety'
    | 'cleaning'
    | 'other-procedures'
    | 'external'
    | 'orca';

interface TabMenuProps {
    activeTab: TabType;
    onTabChange: (tab: TabType) => void;
}

const TabMenu: React.FC<TabMenuProps> = ({ activeTab, onTabChange }) => {
    const tabs = useMemo(
        () => [
            { id: 'all', label: 'All' },
            { id: 'general', label: 'General' },
            { id: 'operational', label: 'Operational' },
            { id: 'regulations', label: 'Regulations & Requirements' },
            { id: 'uscg', label: 'USCG' },
            { id: 'coating', label: 'Coating' },
            { id: 'safety', label: 'Safety/Emergency' },
            { id: 'cleaning', label: 'Cleaning' },
            { id: 'other-procedures', label: 'Other Procedures' },
            { id: 'external', label: 'External IDs' },
            { id: 'orca', label: 'ORCA' },
        ],
        []
    );
    const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    const focusTab = (tabId: TabType) => {
        const target = tabRefs.current[tabId];
        if (target) {
            target.focus();
        }
    };

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key !== 'Tab') return;

            event.preventDefault();
            const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
            const nextIndex = event.shiftKey
                ? (currentIndex - 1 + tabs.length) % tabs.length
                : (currentIndex + 1) % tabs.length;
            const nextTab = tabs[nextIndex].id as TabType;
            onTabChange(nextTab);
            focusTab(nextTab);
        },
        [activeTab, onTabChange, tabs]
    );

    return (
        <TabsContainer role="tablist" onKeyDown={handleKeyDown}>
            {tabs.map((tab) => (
                <li key={tab.id}>
                    <button
                        type="button"
                        className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        ref={(node) => {
                            tabRefs.current[tab.id] = node;
                        }}
                        onClick={() => onTabChange(tab.id as TabType)}
                    >
                        {tab.label}
                    </button>
                </li>
            ))}
        </TabsContainer>
    );
};

export default TabMenu;
